/**
 * `/api/me/school` hooks — a signed-in student's own grade/homeroom.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { myFamilyQueryKey } from './family'
import { apiFetch } from './http'

export interface MeSchoolEntry {
  organizationId: string
  organizationName: string
  schoolLocationId: string
  schoolLocationName: string
  termId: string | null
  gradeLevel: string | null
  homeroom: string | null
  /** The school's configured grade list (campus vocabulary order). */
  gradeOptions: string[]
  /** The school's configured homeroom list (campus vocabulary order). */
  homeroomOptions: string[]
  /**
   * Facts the campus does not collect. Optional: a response cached before
   * PROD-1231 has no such field, and treating that as "collects everything"
   * keeps the pre-existing behaviour rather than hiding a picker that should
   * show.
   */
  hiddenFacts?: string[]
}

export interface MeSchoolResponse {
  schools: MeSchoolEntry[]
}

export interface UpdateMeSchoolInput {
  organizationId: string
  schoolLocationId: string
  gradeLevel?: string
  homeroom?: string | null
}

export const meSchoolQueryKey = ['me', 'school'] as const

export function useMeSchoolQuery(enabled = true) {
  return useQuery<MeSchoolResponse>({
    queryKey: meSchoolQueryKey,
    queryFn: ({ signal }) => apiFetch<MeSchoolResponse>('/api/me/school', { signal }),
    enabled,
    staleTime: 30_000,
  })
}

export function useUpdateMeSchoolMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: UpdateMeSchoolInput) =>
      apiFetch<{ school: MeSchoolEntry }>('/api/me/school', { method: 'PATCH', body: input }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: meSchoolQueryKey }),
        queryClient.invalidateQueries({ queryKey: myFamilyQueryKey }),
      ])
    },
  })
}
